import { useMemo } from 'react';
import { useLingui } from '@lingui/react';
import { Users } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { StatusBadge } from '@/components/ui/status-badge';
import type { WorkflowItemResponse } from '@/types/workflow';
import { WORKFLOW_STATES, getStateLabel, getStateTone } from '@/types/workflow';
import {
  classifyChangeRequestTriage,
  isChangeRequestTerminalState,
  parseChangeRequestPayload,
  type TriageBucket,
} from '@/types/changeRequest';
import {
  responsibleDetail,
  responsibleFor,
  responsibleGroupKey,
  responsibleGroupRank,
  responsibleInitials,
  responsibleName,
  type Responsible,
  type ResponsibleContext,
} from '@/types/workflowResponsible';
import { WorkflowBoardCard } from './WorkflowBoardCard';
import { ChangeRequestTriageGroup } from './ChangeRequestTriageGroup';
import type { PreviewLine } from './approvalPreviewText';

type Item = WorkflowItemResponse & { id: string };
const NO_HELD_BACK: ReadonlyMap<string, PreviewLine | null> = new Map();
const NO_NAMES: ReadonlyMap<string, string> = new Map();

/** One board column of the by-person layout. */
interface ResponsibleColumn {
  key: string;
  responsible: Responsible;
  rank: number;
  name: string;
  items: Item[];
}

interface WorkflowBoardProps {
  items: Item[];
  onOpenDetail: (itemId: string) => void;
  onActionClick: (itemId: string, action: string, currentState: string) => void;
  /** "Approve all" for the Routine change_request bucket; checks the previews first. */
  onBatchApprove?: (items: Item[]) => void;
  batchPending?: boolean;
  /** Requests the last "Approve all" check held back; they move to "Needs your review". */
  heldBack?: ReadonlyMap<string, PreviewLine | null>;
  /**
   * Force the two-bucket change_request triage layout (the change_request scope).
   * When set, the board renders the triage buckets from the change_request items
   * in `items` even if the array also carries other categories (they are ignored
   * here). Defaults to auto-detecting an all-change_request board.
   */
  triage?: boolean;
  /**
   * Columns: one per workflow state (default), or one per responsible (you,
   * each person, each group lead, each waiting role, nobody).
   */
  groupBy?: 'responsible' | 'state';
  /** Who is "you" and how people are named; needed to group by responsible. */
  responsibleContext?: ResponsibleContext;
}

export function WorkflowBoard({
  items,
  onOpenDetail,
  onActionClick,
  onBatchApprove,
  batchPending,
  heldBack = NO_HELD_BACK,
  triage,
  groupBy = 'state',
  responsibleContext,
}: WorkflowBoardProps) {
  const { i18n } = useLingui();
  const unknownName = i18n._({ id: 'common.unknown', message: 'Unknown' });
  const context: ResponsibleContext = useMemo(
    () => responsibleContext ?? { namesByClerkUserId: NO_NAMES, unknownName },
    [responsibleContext, unknownName],
  );

  // Render the two triage buckets ("Needs your review" vs "Routine")
  // either when the caller activates the change_request scope (`triage`) or when
  // a by-status board happens to be all change_request items. The by-person
  // board stays by person: that is the question its reader asked.
  const showTriage =
    triage ||
    (groupBy === 'state' && items.length > 0 && items.every((it) => it.category === 'change_request'));

  const triageBuckets = useMemo(() => {
    const grouped: Record<TriageBucket, Item[]> = {
      needs_handholding: [],
      press_through: [],
    };
    if (!showTriage) return grouped;
    for (const item of items) {
      // Only change_request items belong in the triage buckets, and terminal
      // items (executed/failed/rejected/resolved) are done — keep the worklist live.
      if (item.category !== 'change_request') continue;
      if (isChangeRequestTerminalState(item.current_state)) continue;
      // A request whose preview has a blocker needs a person, whatever its kind.
      const bucket = heldBack.has(item.id)
        ? 'needs_handholding'
        : classifyChangeRequestTriage(parseChangeRequestPayload(item.payload), item.current_state ?? '');
      grouped[bucket].push(item);
    }
    return grouped;
  }, [items, showTriage, heldBack]);

  const itemsByState = useMemo(() => {
    const grouped: Record<string, Item[]> = {};
    for (const state of WORKFLOW_STATES) {
      grouped[state] = [];
    }
    for (const item of items) {
      const state = item.current_state ?? 'open';
      if (grouped[state]) {
        grouped[state].push(item);
      } else {
        // Unknown state — add to open column
        grouped['open'].push(item);
      }
    }
    return grouped;
  }, [items]);

  const responsibleColumns = useMemo(() => {
    if (showTriage || groupBy !== 'responsible') return [];
    const byKey = new Map<string, ResponsibleColumn>();
    for (const item of items) {
      const responsible = responsibleFor(item, context);
      const key = responsibleGroupKey(responsible);
      const column = byKey.get(key);
      if (column) {
        column.items.push(item);
      } else {
        byKey.set(key, {
          key,
          responsible,
          rank: responsibleGroupRank(responsible),
          name: responsibleName(i18n, responsible),
          items: [item],
        });
      }
    }
    return [...byKey.values()].sort(
      (a, b) => a.rank - b.rank || a.name.localeCompare(b.name, i18n.locale),
    );
  }, [items, showTriage, groupBy, context, i18n]);

  if (showTriage) {
    return (
      <div
        className="grid min-h-0 flex-1 grid-cols-1 gap-4 md:grid-cols-[repeat(2,minmax(240px,1fr))]"
        data-testid="workflow-board-triage"
      >
        <ChangeRequestTriageGroup
          bucket="needs_handholding"
          items={triageBuckets.needs_handholding}
          onOpenDetail={onOpenDetail}
          onActionClick={onActionClick}
          heldBack={heldBack}
        />
        <ChangeRequestTriageGroup
          bucket="press_through"
          items={triageBuckets.press_through}
          onOpenDetail={onOpenDetail}
          onActionClick={onActionClick}
          onBatchApprove={onBatchApprove}
          batchPending={batchPending}
          heldBack={heldBack}
        />
      </div>
    );
  }

  if (groupBy === 'responsible') {
    if (responsibleColumns.length === 0) {
      return (
        <div
          className="flex items-center justify-center h-24 rounded-lg bg-muted/30 text-xs text-muted-foreground"
          data-testid="workflow-board-responsible"
        >
          {i18n._({ id: 'workflow.board.empty', message: 'No items' })}
        </div>
      );
    }
    return (
      // One column per responsible; the row scrolls sideways when they do not fit.
      <div
        className="grid min-h-0 flex-1 grid-flow-col auto-cols-[minmax(220px,1fr)] gap-4 overflow-x-auto"
        data-testid="workflow-board-responsible"
      >
        {responsibleColumns.map((column) => (
          <div key={column.key} className="flex min-h-0 min-w-0 flex-col" data-testid={`board-column-${column.key}`}>
            <ResponsibleColumnHeader column={column} />
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-lg bg-muted/30 p-2">
              {column.items.map((item) => (
                <WorkflowBoardCard
                  key={item.id}
                  item={item}
                  onOpenDetail={onOpenDetail}
                  onActionClick={onActionClick}
                  markNotTaken={column.key !== 'nobody'}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    // One column per WORKFLOW_STATES entry (five).
    <div
      className="grid min-h-0 flex-1 grid-cols-[repeat(5,minmax(200px,1fr))] gap-4 overflow-x-auto"
      data-testid="workflow-board"
    >
      {WORKFLOW_STATES.map((state) => {
        const stateItems = itemsByState[state];
        return (
          <div key={state} className="flex min-h-0 min-w-0 flex-col">
            {/* Column header */}
            <div className="flex items-center justify-between mb-3 px-1">
              <StatusBadge tone={getStateTone(state)} label={getStateLabel(i18n, state)} />
              <span className="text-xs text-muted-foreground font-medium">
                {stateItems.length}
              </span>
            </div>

            {/* Column body */}
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-lg bg-muted/30 p-2">
              {stateItems.length === 0 ? (
                <div className="flex items-center justify-center h-24 text-xs text-muted-foreground">
                  {i18n._({ id: 'workflow.board.empty', message: 'No items' })}
                </div>
              ) : (
                stateItems.map((item) => (
                  <WorkflowBoardCard
                    key={item.id}
                    item={item}
                    onOpenDetail={onOpenDetail}
                    onActionClick={onActionClick}
                    responsible={responsibleContext ? responsibleFor(item, responsibleContext) : undefined}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** A by-person column head: who, why (for a group lead), and how many items. */
function ResponsibleColumnHeader({ column }: { column: ResponsibleColumn }) {
  const { i18n } = useLingui();
  const { responsible } = column;
  const isPerson = responsible.kind === 'person' || responsible.kind === 'lead';
  const detail = responsible.kind === 'lead' ? responsibleDetail(i18n, responsible) : null;
  return (
    <div className="flex items-center justify-between gap-2 mb-3 px-1 min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        {isPerson ? (
          <Avatar className="h-6 w-6 shrink-0">
            <AvatarFallback className="text-[10px]">{responsibleInitials(responsible)}</AvatarFallback>
          </Avatar>
        ) : (
          <Users className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        )}
        <div className="min-w-0 leading-tight">
          <div
            className={isPerson ? 'text-sm font-medium truncate' : 'text-sm text-muted-foreground truncate'}
            title={column.name}
          >
            {column.name}
          </div>
          {detail && (
            <div className="text-xs text-muted-foreground truncate" title={detail}>
              {detail}
            </div>
          )}
        </div>
      </div>
      <span className="text-xs text-muted-foreground font-medium shrink-0">{column.items.length}</span>
    </div>
  );
}
